/**
 * Centralized color definitions for status indicators across the application.
 * These colors align with Material-UI's theme palette and provide consistent
 * visual feedback for different states and categories.
 */

// Base status colors (aligned with MUI theme)
export const STATUS_COLORS = {
  success: '#4CAF50',     // Green - for successful/active states
  error: '#F44336',       // Red - for errors/failures/denied states
  warning: '#FF9800',     // Orange - for warnings/pending actions
  info: '#2196F3',        // Blue - for informational states
  inactive: '#9E9E9E',    // Gray - for inactive/disabled states
  pending: '#FFC107',     // Yellow/Amber - for pending states
  default: '#757575',     // Medium Gray - for unknown/default states
} as const;

// Domain-specific colors
export const DOMAIN_COLORS = {
  // Absence categories
  vacation: '#2196F3',        // Blue
  sickDays: '#FF9800',        // Orange
  medicalLeave: '#F44336',    // Red
  maternity: '#E91E63',       // Pink
  parental: '#00BCD4',        // Cyan
  familyHoliday: '#009688',   // Teal
  earlyLeave: '#3F51B5',      // Indigo
  lateOnShift: '#795548',       // Brown
  
  // Shop preferences
  trusted: '#FFA726',         // Orange (matching map markers)
  dnu: '#F44336',             // Red (Do Not Use)
  
  // Special states
  revoked: '#FF5722',         // Deep Orange
  cancelled: '#9E9E9E',       // Gray
} as const;

/**
 * Get color for active/inactive status
 */
export const getActiveStatusColor = (isActive: boolean): string => {
  return isActive ? STATUS_COLORS.success : STATUS_COLORS.inactive;
};

/**
 * Get color for absence request status
 * @param status - 1: Pending, 2: Approved, 3: Denied, 4: Cancelled, 5: Revoked
 */
export const getAbsenceStatusColor = (status: number): string => {
  const statusColors: Record<number, string> = {
    1: STATUS_COLORS.pending,     // Pending
    2: STATUS_COLORS.success,     // Approved
    3: STATUS_COLORS.error,       // Denied
    4: DOMAIN_COLORS.cancelled,   // Cancelled
    5: DOMAIN_COLORS.revoked,     // Revoked
  };
  return statusColors[status] || STATUS_COLORS.default;
};

/**
 * Get status name for absence request
 * @param status - 1: Pending, 2: Approved, 3: Denied, 4: Cancelled, 5: Revoked
 */
export const getAbsenceStatusName = (status: number): string => {
  const statusNames: Record<number, string> = {
    1: 'Pending',
    2: 'Approved',
    3: 'Denied',
    4: 'Cancelled',
    5: 'Revoked',
  };
  return statusNames[status] || 'Unknown';
};

/**
 * Get color for absence category
 * @param category - 1: Vacation, 2: Sick Days, 3: Medical Leave, 5: Maternity, 6: Parental, 7: Family Holiday
 */
export const getAbsenceCategoryColor = (category: number): string => {
  const categoryColors: Record<number, string> = {
    1: DOMAIN_COLORS.vacation,
    2: DOMAIN_COLORS.sickDays,
    3: DOMAIN_COLORS.medicalLeave,
    5: DOMAIN_COLORS.maternity,
    6: DOMAIN_COLORS.parental,
    7: DOMAIN_COLORS.familyHoliday,
    8: DOMAIN_COLORS.earlyLeave,
    9: DOMAIN_COLORS.lateOnShift,
  };
  return categoryColors[category] || STATUS_COLORS.default;
};

/**
 * Get category name for absence
 * @param category - 1: Vacation, 2: Sick Days, 3: Medical Leave, 5: Maternity, 6: Parental, 7: Family Holiday
 */
export const getAbsenceCategoryName = (category: number): string => {
  const categoryNames: Record<number, string> = {
    1: 'Vacation',
    2: 'Sick Days',
    3: 'Medical Leave',
    5: 'Maternity',
    6: 'Parental',
    7: 'Family Holiday',
    8: 'Early Leave',
    9: 'Late On Shift',
  };
  return categoryNames[category] || 'Unknown';
};

/**
 * Get color for shop preference
 * @param preference - 0: Trusted, 1: DNU (Do Not Use)
 */
export const getShopPreferenceColor = (preference: number): string => {
  return preference === 0 ? DOMAIN_COLORS.trusted : DOMAIN_COLORS.dnu;
};

/**
 * Get label for shop preference
 * @param preference - 0: Trusted, 1: DNU (Do Not Use)
 */
export const getShopPreferenceLabel = (preference: number): string => {
  return preference === 0 ? 'Trusted' : 'DNU';
};

/**
 * Get color for shop request status
 * @param status - 0: Pending, 1: Approved, 2: Rejected
 */
export const getShopRequestStatusColor = (status: number): string => {
  const statusColors: Record<number, string> = {
    0: STATUS_COLORS.pending,   // Pending
    1: STATUS_COLORS.success,   // Approved
    2: STATUS_COLORS.error,     // Rejected
  };
  return statusColors[status] || STATUS_COLORS.default;
};

/**
 * Get label for shop request status
 * @param status - 0: Pending, 1: Approved, 2: Rejected
 */
export const getShopRequestStatusLabel = (status: number): string => {
  const statusLabels: Record<number, string> = {
    0: 'Pending',
    1: 'Approved',
    2: 'Rejected',
  };
  return statusLabels[status] || 'Unknown';
};

/**
 * Get color for integration sync status
 * @param status - "Success", "Failed", "Pending"
 */
export const getIntegrationSyncStatusColor = (status: string | null): string => {
  if (!status) return STATUS_COLORS.default;
  
  const statusColors: Record<string, string> = {
    Success: STATUS_COLORS.success,
    Failed: STATUS_COLORS.error,
    Pending: STATUS_COLORS.warning,
  };
  return statusColors[status] || STATUS_COLORS.default;
};

/**
 * Get color for vehicle/trailer status
 * @param status - "Active", "Inactive", or other status strings
 */
export const getVehicleStatusColor = (status: string): string => {
  const statusColors: Record<string, string> = {
    Active: STATUS_COLORS.success,
    Inactive: STATUS_COLORS.inactive,
  };
  return statusColors[status] || STATUS_COLORS.default;
};

/**
 * Get color for user account status.
 * @param status - 0: Pending Setup, 1: Active, 2: Archived
 */
export const getUserStatusColor = (status: number): string => {
  const statusColors: Record<number, string> = {
    0: STATUS_COLORS.warning,
    1: STATUS_COLORS.success,
    2: STATUS_COLORS.inactive,
  };
  return statusColors[status] || STATUS_COLORS.default;
};

/**
 * Get label for user account status.
 * @param status - 0: Pending Setup, 1: Active, 2: Archived
 */
export const getUserStatusLabel = (status: number): string => {
  const statusLabels: Record<number, string> = {
    0: 'Pending Setup',
    1: 'Active',
    2: 'Archived',
  };
  return statusLabels[status] || 'Unknown';
};

/**
 * Get color for HOS duty status
 * @param status - "DRIVING", "SLEEPER", "OFF_DUTY", "ON_DUTY"
 */
export const getHosDutyStatusColor = (status: string): string => {
  const statusColors: Record<string, string> = {
    DRIVING: STATUS_COLORS.success,    // Green
    SLEEPER: STATUS_COLORS.info,       // Blue
    OFF_DUTY: STATUS_COLORS.inactive,  // Gray
    ON_DUTY: STATUS_COLORS.warning,    // Orange
  };
  return statusColors[status] || STATUS_COLORS.default;
};

/**
 * Get label for HOS duty status
 * @param status - "DRIVING", "SLEEPER", "OFF_DUTY", "ON_DUTY"
 */
export const getHosDutyStatusLabel = (status: string): string => {
  const statusLabels: Record<string, string> = {
    DRIVING: 'Driving',
    SLEEPER: 'Sleeper',
    OFF_DUTY: 'Off Duty',
    ON_DUTY: 'On Duty',
  };
  return statusLabels[status] || status;
};

/**
 * MUI Chip color for IFTA report generation status.
 */
export const getIftaReportStatusChipColor = (
  status: string
): "success" | "error" | "info" | "default" => {
  switch (status.toLowerCase()) {
    case "completed":
      return "success";
    case "failed":
      return "error";
    case "queued":
      return "info";
    default:
      return "default";
  }
};

/**
 * Display label for IFTA report generation status.
 */
export const getIftaReportStatusLabel = (status: string): string => {
  switch (status.toLowerCase()) {
    case "completed":
      return "Completed";
    case "failed":
      return "Failed";
    case "queued":
      return "Queued";
    default:
      return status;
  }
};
